/**
 * The behaviour the typed date and time fields share: the text box shows the
 * value's friendly label until the operator types, a complete entry is applied
 * as soon as it reads as a value, and the picker below stays open while focus
 * is anywhere inside the field. Leaving with text that does not read as a
 * value puts back the value from before the typing began — a keystroke that
 * happened to parse on the way ("7" of "7:30") never sticks by accident.
 */
import { useRef, useState } from 'react';
import type { FocusEvent, KeyboardEvent } from 'react';

interface PickerFieldArgs {
  value: string;
  onChange: (value: string) => void;
  /** The value a piece of typed text stands for, or null when it is not one yet. */
  parse: (text: string) => string | null;
  /** The friendly text a value is shown as. */
  label: (value: string) => string;
}

export function usePickerField({ value, onChange, parse, label }: PickerFieldArgs) {
  const [typed, setTyped] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const valueBeforeTyping = useRef(value);

  const close = () => {
    setTyped(null);
    setOpen(false);
  };

  /** Close, first undoing any typing that never became a complete value. */
  const settle = () => {
    const abandoned = typed !== null && typed.trim() !== '' && parse(typed) === null;
    if (abandoned && value !== valueBeforeTyping.current) onChange(valueBeforeTyping.current);
    close();
  };

  const onInputChange = (text: string) => {
    if (typed === null) valueBeforeTyping.current = value;
    setTyped(text);
    setOpen(true);
    if (text.trim() === '') {
      onChange('');
      return;
    }
    const parsed = parse(text);
    if (parsed) onChange(parsed);
  };

  /** Apply a value picked from the list or calendar, and close the picker. */
  const choose = (picked: string) => {
    onChange(picked);
    close();
  };

  // Leaving the input for the picker below keeps it open; leaving the field closes it.
  const onBlur = (e: FocusEvent<HTMLDivElement>) => {
    if (wrapperRef.current?.contains(e.relatedTarget as Node | null)) return;
    settle();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape' && open) {
      e.preventDefault();
      settle();
    } else if (e.key === 'ArrowDown' && !open) {
      setOpen(true);
    }
  };

  return {
    wrapperRef,
    open,
    setOpen,
    /** What the operator has typed since focusing, or null when untouched. */
    typed,
    display: typed ?? label(value),
    onInputChange,
    choose,
    onBlur,
    onKeyDown,
  };
}

/** The popover keeps focus in the input while it is clicked, so the field never
 *  sees a blur mid-pick and the picker doesn't close under the pointer. */
export const keepInputFocus = (e: { preventDefault: () => void }) => e.preventDefault();
